<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { aiService, type AIProjectionData } from '@/api/aiService'

const projectionData = ref<AIProjectionData | null>(null)
const loading = ref(true)
const selectedLang = ref<'sw' | 'en'>('sw')

const fetchProjections = async () => {
  loading.value = true
  try {
    const data = await aiService.getFarmProjections(selectedLang.value)
    projectionData.value = data
  } catch (err) {
    console.error('Failed to load AI projections:', err)
  } finally {
    loading.value = false
  }
}

const toggleLang = (lang: 'sw' | 'en') => {
  selectedLang.value = lang
  fetchProjections()
}

onMounted(fetchProjections)
</script>

<template>
  <div class="relative bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-6 shadow-xl border border-indigo-500/20 overflow-hidden font-sans">
    <!-- Glowing Background Highlights -->
    <div class="absolute -top-12 -right-12 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
    <div class="absolute -bottom-12 -left-12 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>

    <!-- Header & Language Controls -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 relative z-10">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-2xl bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl shadow-lg shadow-indigo-500/30">
          🔮
        </div>
        <div>
          <h2 class="text-base font-bold text-white tracking-wide">
            {{ projectionData?.title || (selectedLang === 'sw' ? 'Utabiri na Mkakati wa Kukuza Faida (AI Projections)' : 'AI Profit & Yield Projections') }}
          </h2>
          <p class="text-xs text-indigo-200/80 mt-0.5">
            {{ projectionData?.subtitle || (selectedLang === 'sw' ? 'Tathmini ya siku 30 zijazo' : '30-Day predictive farm analytics') }}
          </p>
        </div>
      </div>

      <!-- Controls -->
      <div class="flex items-center gap-2">
        <div class="flex items-center bg-slate-800/80 p-0.5 rounded-xl border border-slate-700">
          <button
            @click="toggleLang('sw')"
            class="px-3 py-1 text-xs font-bold rounded-lg transition-all"
            :class="selectedLang === 'sw' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'"
          >
            Kiswahili
          </button>
          <button
            @click="toggleLang('en')"
            class="px-3 py-1 text-xs font-bold rounded-lg transition-all"
            :class="selectedLang === 'en' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'"
          >
            English
          </button>
        </div>

        <button
          @click="fetchProjections"
          :disabled="loading"
          class="p-2 bg-slate-800/80 text-slate-300 hover:text-white rounded-xl border border-slate-700 transition-colors disabled:opacity-50"
          title="Refresh AI Predictions"
        >
          <svg :class="loading ? 'animate-spin' : ''" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading Skeleton -->
    <div v-if="loading" class="py-12 flex items-center justify-center gap-3 text-indigo-300 text-sm font-medium">
      <div class="animate-spin h-6 w-6 border-2 border-indigo-400 border-t-transparent rounded-full"></div>
      Analyzing historical farm yield, feed logs & financial trends...
    </div>

    <!-- Content -->
    <div v-else-if="projectionData" class="space-y-6 relative z-10">
      <!-- Top Metrics Highlights Grid -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <!-- Metric 1: Projected Production Volume -->
        <div class="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between">
          <div>
            <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              {{ selectedLang === 'sw' ? 'Makadirio ya Uzalishaji (30d)' : 'Forecasted Yield (30 Days)' }}
            </p>
            <div class="flex items-baseline gap-2 mt-2">
              <span class="text-2xl font-black text-white">
                {{ projectionData.metrics.projected_30_yield.toLocaleString() }}
              </span>
              <span class="text-xs font-bold text-slate-300">Units</span>
            </div>
          </div>
          <div class="mt-3 flex items-center gap-1.5 text-xs font-bold text-emerald-400">
            <span>↗ +{{ Math.abs(projectionData.metrics.yield_growth_trend) }}%</span>
            <span class="text-[10px] text-slate-400 font-normal">vs previous month</span>
          </div>
        </div>

        <!-- Metric 2: Net Profit Projection -->
        <div class="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between">
          <div>
            <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              {{ selectedLang === 'sw' ? 'Makadirio ya Faida Safi' : 'Projected Net Margin' }}
            </p>
            <div class="flex items-baseline gap-1 mt-2">
              <span class="text-xs text-slate-400 font-semibold">TSH</span>
              <span class="text-2xl font-black text-emerald-400">
                {{ projectionData.metrics.projected_profit.toLocaleString() }}
              </span>
            </div>
          </div>
          <div class="mt-3 flex items-center gap-1.5 text-xs font-bold text-indigo-300">
            <span>📊 {{ projectionData.metrics.projected_margin_pct }}% Net Margin</span>
          </div>
        </div>

        <!-- Metric 3: Feed Efficiency Index -->
        <div class="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between">
          <div>
            <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              {{ selectedLang === 'sw' ? 'Kipimo cha Ufanisi wa Lishe' : 'Feed Conversion Index' }}
            </p>
            <div class="flex items-baseline gap-2 mt-2">
              <span class="text-2xl font-black text-amber-400">
                {{ projectionData.metrics.feed_efficiency_score }}%
              </span>
              <span class="text-xs font-semibold text-slate-400">Score</span>
            </div>
          </div>
          <div class="mt-3 w-full bg-slate-800 h-2 rounded-full overflow-hidden">
            <div
              class="h-full bg-gradient-to-r from-amber-500 to-emerald-400 rounded-full transition-all duration-500"
              :style="{ width: `${projectionData.metrics.feed_efficiency_score}%` }"
            ></div>
          </div>
        </div>
      </div>

      <!-- AI Strategic Recommendations List -->
      <div class="bg-slate-900/90 border border-indigo-500/20 rounded-2xl p-5">
        <h3 class="text-xs font-bold text-indigo-300 uppercase tracking-wider mb-3 flex items-center gap-2">
          <span>💡</span>
          <span>{{ selectedLang === 'sw' ? 'Mapendekezo ya AI ya Kukuza Uzalishaji na Kupunguza Gharama' : 'AI Profit & Yield Maximization Advice' }}</span>
        </h3>
        <ul class="space-y-2.5">
          <li
            v-for="(rec, idx) in projectionData.recommendations"
            :key="idx"
            class="text-xs text-slate-200 leading-relaxed bg-slate-800/50 p-3 rounded-xl border border-slate-800 flex items-start gap-2.5"
          >
            <span class="text-indigo-400 font-bold flex-shrink-0 mt-0.5">•</span>
            <span v-html="rec.replace(/\*\*(.*?)\*\*/g, '<strong class=\'text-white font-bold\'>$1</strong>')"></span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
